import {
  Box,
  Divider,
  FormHelperText,
  Grid,
  Stack,
  Typography,
} from '@mui/material';
import { FieldArray, Form, Formik, getIn } from 'formik';
import { isArray, isEmpty, isString } from 'lodash';
import { useMemo, useRef, useState } from 'react';
import Button, { CloseButton } from '~/components/common/Button';
import Modal from '~/components/common/Modal';
import { DateElement, SpecialHour } from '~/models/user';
import themes from '~/themes';
import { Field } from '~/components/common/Formik';
import GroupRadioButton from '~/components/common/GroupRadioButton';
import { HoursSelect } from '~/pages/AdminOptions/Stores/Detail/components/DeliveryHours/components/HoursSelect';
import Tooltip from '~/components/common/Tooltip';
import DeleteIcon from '~/assets/images/icons/delete-bin-line.png';
import AddIcon from '~/assets/images/icons/add-black.svg';
import InfoIcon from '~/assets/images/icons/info-gray.svg';
import { transformTypeToTitle } from '~/utils/common';
import { AddShift, AddSpecialShift } from '~/utils/schema/driver';
import moment from 'moment';
import {
  DEFAULT_SHIFT,
  DEFAULT_SPECIAL_SHIFT,
  GAP_TIME_OPTIONS,
} from '../../../constants';

export type HourError = {
  id: string;
  error: string;
};

const defaultShift = {
  start_time: '09:00',
  end_time: '21:00',
  pause_assign: 0,
};

type Props = {
  open: boolean;
  isSpecial?: boolean;
  data: Array<
    | DateElement &
        SpecialHour & {
          weekDay?: string;
          name?: string;
        }
  >;
  onClose: () => void;
  onDelete?: () => void;
  onSave: (val: any) => void;
  invalidDays?: any;
};

const DialogAddShiftSchedule = ({
  open,
  isSpecial = false,
  data,
  onClose,
  onSave,
  onDelete,
  invalidDays,
}: Props) => {
  const formRef = useRef(null);
  const [dateError, setDateError] = useState('');

  const validationSchema = useMemo(
    () => (isSpecial ? AddSpecialShift : AddShift),
    [isSpecial],
  );

  const title = useMemo(() => {
    if (isSpecial) {
      return isEmpty(data) ? 'Add Special Hours' : data[0]?.name;
    }
    return data?.map((day) => day?.weekDay)?.join(', ') || '';
  }, [data, isSpecial]);

  const initialValues = useMemo(() => {
    if (isEmpty(data)) return isSpecial ? DEFAULT_SPECIAL_SHIFT : DEFAULT_SHIFT;
    return {
      ...data[0],
      ...(isSpecial && {
        name: data[0].name,
        start_date: moment(data[0].start_date).format('MMM D, YYYY'),
        end_date: moment(data[0].end_date).format('MMM D, YYYY'),
      }),
      all_hour: data[0].all_hour,
      available: data[0].available,
      shifts: !isEmpty(data[0].shifts) ? data[0].shifts : [defaultShift],
    };
  }, [data, isSpecial]);

  const onSubmit = (values) => {
    if (isSpecial) {
      if (!values.start_date || !values.end_date) {
        return setDateError('Please enter Start Date or End Date');
      }
      values.start_date = moment(values.start_date)
        .local()
        .format('YYYY-MM-DD');
      values.end_date = moment(values.end_date).local().format('YYYY-MM-DD');
    }

    onSave({ ...data[0], ...values });
    return onClose();
  };

  const renderHeader = () => (
    <Stack
      sx={{
        p: themes.spacing(1.6),
        borderBottom: '1px solid #E5E3F4',
        position: 'sticky',
        top: 0,
        zIndex: 10,
        backgroundColor: 'white',
        borderTopRightRadius: '8px !important',
        borderTopLeftRadius: '8px !important',
      }}
      flexDirection='row'
      justifyContent='space-between'
      alignItems='center'
    >
      <Typography
        sx={{
          fontSize: 18,
          fontWeight: 500,
        }}
      >
        {isSpecial ? title : transformTypeToTitle(title)}
      </Typography>
      <CloseButton background='white' iconSize={11} onClick={onClose} />
    </Stack>
  );

  const renderBottom = () => (
    <Stack
      sx={{
        position: 'sticky',
        bottom: 0,
        background: 'white',
        flexDirection: 'row',
        justifyContent: 'space-between',
        alignItems: 'center',
        p: 1.6,
        gap: 1.6,
        borderTop: '1px solid #E5E3F4',
        borderBottomRightRadius: '8px !important',
        borderBottomLeftRadius: '8px !important',
      }}
    >
      <Box>
        {isSpecial && !isEmpty(data) && (
          <Button
            buttonType='text'
            noRounder
            sx={{
              minWidth: 'unset !important',
              padding: '4px 16px',
              backgroundColor: '#F8F8FC !important',
            }}
            onClick={() => {
              onDelete();
              onClose();
            }}
          >
            <Typography
              sx={{
                color: themes.color.violet500,
                fontWeight: 500,
                display: 'flex',
                flexDirection: 'row',
                alignItems: 'center',
                gap: 0.5,
              }}
            >
              <img
                src={DeleteIcon}
                alt=''
                style={{
                  width: '16px',
                  height: '16px',
                  paddingBottom: '2px',
                }}
              />
              Delete
            </Typography>
          </Button>
        )}
      </Box>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'row',
          alignItems: 'center',
          gap: 1.6,
        }}
      >
        <Button
          buttonType='text'
          noRounder
          sx={{
            minWidth: 'unset !important',
            padding: '4px 16px',
            backgroundColor: '#F8F8FC !important',
          }}
        >
          <Typography
            sx={{
              color: themes.color.violet500,
              fontWeight: 500,
            }}
            onClick={onClose}
          >
            Cancel
          </Typography>
        </Button>
        <Button
          noRounder
          buttonType='primary-dark'
          sx={{
            minWidth: 'unset !important',
            padding: '4px 16px',
          }}
          onClick={() => formRef.current?.handleSubmit()}
        >
          <Typography>Save</Typography>
        </Button>
      </Box>
    </Stack>
  );

  const renderShifts = ({ values, touched, errors, setFieldValue }) => {
    const { shifts } = values;

    const helperText =
      isArray(getIn(touched, 'shifts')) && isString(getIn(errors, 'shifts'))
        ? getIn(errors, 'shifts')
        : '';

    return (
      <Stack
        flexDirection='column'
        gap={1.6}
        sx={{
          background: themes.bg.lightPurple,
          p: 1.6,
        }}
      >
        <Stack direction='column'>
          <Typography color={themes.color.black}>Shifts</Typography>
          <FieldArray name='shifts'>
            {({ remove, push }) => (
              <Stack>
                {shifts?.map(
                  ({ start_time, end_time, pause_assign }, index) => (
                    <Stack
                      // eslint-disable-next-line react/no-array-index-key
                      key={index}
                      direction='row'
                      gap={1.6}
                      sx={{
                        [themes.breakpoints.down('md')]: {
                          flexDirection: 'column',
                        },
                        mt: 1.6,
                      }}
                    >
                      <HoursSelect
                        legend={
                          index === 0 ? (
                            <Typography
                              variant='body2'
                              sx={{
                                color: '#8C95BA',
                                pb: 1.5,
                              }}
                            >
                              Start Time
                            </Typography>
                          ) : (
                            ''
                          )
                        }
                        timeGap={15}
                        value={start_time}
                        onChange={(e: any) => {
                          const { value } = e.target;
                          setFieldValue(`shifts[${index}].start_time`, value);
                        }}
                        error={
                          !!getIn(touched, `shifts[${index}].start_time`) &&
                          !!getIn(errors, `shifts[${index}].start_time`)
                        }
                        helperText={
                          getIn(touched, `shifts[${index}].start_time`) &&
                          getIn(errors, `shifts[${index}].start_time`)
                        }
                      />
                      <HoursSelect
                        legend={
                          index === 0 ? (
                            <Typography
                              variant='body2'
                              sx={{
                                color: '#8C95BA',
                                pb: 1.5,
                              }}
                            >
                              End Time
                            </Typography>
                          ) : (
                            ''
                          )
                        }
                        timeGap={15}
                        value={end_time}
                        onChange={(e: any) => {
                          const { value } = e.target;
                          setFieldValue(`shifts[${index}].end_time`, value);
                        }}
                        error={
                          !!getIn(touched, `shifts[${index}].end_time`) &&
                          !!getIn(errors, `shifts[${index}].end_time`)
                        }
                        helperText={
                          getIn(touched, `shifts[${index}].end_time`) &&
                          getIn(errors, `shifts[${index}].end_time`)
                        }
                      />
                      <Field.Select
                        fullWidth
                        name={`shifts[${index}].pause_assign`}
                        value={pause_assign}
                        onChange={(e: any) => {
                          const { value } = e.target;
                          setFieldValue(
                            `shifts[${index}].pause_assign`,
                            value || 0,
                          );
                        }}
                        options={[
                          {
                            value: 0,
                            label: 'None',
                          },
                          ...GAP_TIME_OPTIONS,
                        ]}
                        formControlProps={{
                          sx: {
                            '& > div': {
                              background: 'white',
                            },
                          },
                        }}
                        legend={
                          index === 0 ? (
                            <Stack
                              flexDirection='row'
                              alignItems='center'
                              gap={0.5}
                              sx={{
                                pb: 1.5,
                              }}
                            >
                              <Typography
                                variant='body2'
                                sx={{
                                  color: '#8C95BA',
                                }}
                              >
                                Pause auto-assignment{' '}
                              </Typography>
                              <Tooltip
                                title={
                                  <Typography
                                    sx={{ whiteSpace: 'break-spaces' }}
                                  >
                                    Set the cut-off time before the shift ends
                                    to stop assigning new tasks.
                                  </Typography>
                                }
                                arrow
                              >
                                <img
                                  src={InfoIcon}
                                  alt=''
                                  style={{
                                    marginBottom: '2px',
                                  }}
                                />
                              </Tooltip>
                            </Stack>
                          ) : (
                            ''
                          )
                        }
                      />
                      {shifts?.length !== 1 && (
                        <Stack
                          onClick={() => remove(index)}
                          sx={{
                            pt: index === 0 ? '46px' : '8px',
                            cursor: 'pointer',
                            [themes.breakpoints.down('md')]: {
                              pt: '0px',
                              pr: '4px',
                              alignItems: 'flex-end',
                            },
                          }}
                          flexDirection='column'
                        >
                          <img
                            src={DeleteIcon}
                            alt=''
                            style={{
                              width: '16px',
                              height: '16px',
                            }}
                          />
                        </Stack>
                      )}
                    </Stack>
                  ),
                )}
                <Stack flexDirection='row' gap={1.6}>
                  <Button
                    noRounder
                    buttonType='text'
                    sx={{
                      mt: 2,
                      gap: '4px',
                      width: 'fit-content',
                      color: `${themes.color.violet500} !important`,
                      minWidth: 'unset !important',
                    }}
                    onClick={() => {
                      push(defaultShift);
                    }}
                  >
                    <img src={AddIcon} alt='' />
                    Add shift
                  </Button>
                </Stack>
              </Stack>
            )}
          </FieldArray>
          {helperText && <FormHelperText error>{helperText}</FormHelperText>}
        </Stack>
      </Stack>
    );
  };

  const renderSchedule = ({ values, setFieldValue, touched, errors }) => (
    <Stack
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: 1.6,
        p: 1.6,
        color: themes.color.violet500,
        flex: 1,
      }}
    >
      <Stack flexDirection='column' gap={1}>
        <Typography>Schedule</Typography>
        <Stack flexDirection='row' alignItems='center'>
          <Field.SwitchButton noText name='available' />
          <Typography>Driver is scheduled to work</Typography>
        </Stack>
      </Stack>
      {values.available && (
        <>
          <Box>
            <Typography>Shift hours</Typography>
            <GroupRadioButton
              name='all_hour'
              sx={{
                'ml': -0.5,
                'mt': 1,
                'gap': '5px 0px !important',
                '& > label': {
                  border: 'none',
                  padding: 0.5,
                  fontWeight: 'normal',
                },
              }}
              value={values.all_hour}
              onChange={(e) => {
                const value = JSON.parse(e.target.value);
                setFieldValue('all_hour', value);
              }}
              options={[
                { value: true, label: '24 hours' },
                { value: false, label: 'Specific hours' },
              ]}
            />
          </Box>
          {!values.all_hour &&
            renderShifts({ values, setFieldValue, touched, errors })}
        </>
      )}
    </Stack>
  );

  return (
    <Formik
      initialValues={initialValues}
      onSubmit={onSubmit}
      validationSchema={validationSchema}
      enableReinitialize
      innerRef={formRef}
    >
      {({ values, setFieldValue, touched, errors: formErrors }) => (
        <Modal
          open={open}
          onClose={onClose}
          disableCloseOutside
          PaperProps={{
            sx: {
              '& .MuiDialogContent-root': {
                p: 0,
                ...(isSpecial &&
                  (!values.available ||
                    (values.available && values.all_hour)) && {
                    overflowY: 'visible',
                  }),
              },
              'borderRadius': '8px !important',
              'maxWidth': '600px',
              ...(isSpecial &&
                (!values.available ||
                  (values.available && values.all_hour)) && {
                  overflowY: 'visible',
                }),
            },
          }}
        >
          <Form
            onKeyDown={(e) => {
              if (e.code === 'Enter') {
                e.preventDefault();
              }
            }}
          >
            {renderHeader()}
            {isSpecial && (
              <Stack
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  p: 1.6,
                  pb: 0,
                  color: themes.color.violet500,
                }}
              >
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <Field.TextInput name='name' legend='Name' />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Field.DateSelector
                      legend='Start Date'
                      name='start_date'
                      dateFormat
                      disablePastDate
                      timeFormat={false}
                      invalidDays={invalidDays}
                      onApply={(_, val) => {
                        setFieldValue(
                          'start_date',
                          moment(val).local().format('MMM D, YYYY'),
                        );
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Field.DateSelector
                      name='end_date'
                      legend='End Date'
                      dateFormat
                      disablePastDate
                      invalidDays={invalidDays}
                      onApply={(_, val) => {
                        setFieldValue(
                          'end_date',
                          moment(val).local().format('MMM D, YYYY'),
                        );
                      }}
                    />
                  </Grid>
                </Grid>
                {dateError ? (
                  <FormHelperText error>{dateError}</FormHelperText>
                ) : null}
                <Divider sx={{ mt: 1.6 }} />
              </Stack>
            )}
            {renderSchedule({
              values,
              setFieldValue,
              touched,
              errors: formErrors,
            })}
            {renderBottom()}
          </Form>
        </Modal>
      )}
    </Formik>
  );
};

export default DialogAddShiftSchedule;
